import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  NgZone,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { CustomFormControlComponent } from '../../abstracts/custom-form-control.component';
import { MdcFormFieldComponent } from '../mdc-form-field/mdc-form-field.component';
import { LabelValue } from '../models/label-value.model';

@Component({
  selector: 'mdc-dropdown',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MdcFormFieldComponent, MatSelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MdcDropdownComponent),
      multi: true
    }
  ],
  template: `
    <mdc-form-field [noValidation]="noValidation" [label]="label">
      <mat-select
        #matSelect
        panelClass="custom-mdc-dropdown-panel"
        [formControl]="control"
        [compareWith]="compareWith"
        [multiple]="multiple"
        (closed)="onPristineTouch()"
        (selectionChange)="selectionChange()">
        @if (includeEmptyOption && !multiple) {
          <mat-option />
        }
        @for (option of _options; track option) {
          <mat-option [value]="option.value">
            {{ option.label }}
          </mat-option>
        }
      </mat-select>
    </mdc-form-field>
  `
})
export class MdcDropdownComponent<T> extends CustomFormControlComponent {
  @ViewChild('matSelect') matSelect: MatSelect;
  @Input({ transform: booleanAttribute }) noValidation = false;
  @Input({ transform: booleanAttribute }) includeEmptyOption = false;
  @Input({ transform: booleanAttribute }) multiple = false;
  @Input() label = '';
  @Input() labelKey: string;
  @Input() valueKey: string;
  @Input() set options(options: T[]) {
    this._options = options.map((option: T) =>
      this.labelKey
        ? ({ label: option[this.labelKey], value: this.valueKey ? option[this.valueKey] : option } as LabelValue<T>)
        : ({ label: option, value: option } as LabelValue<T>)
    );
  }
  cd = inject(ChangeDetectorRef);
  ngZone = inject(NgZone);

  _options: LabelValue<T>[] = [];

  /*
   * IMPORTANT: Use an arrow function for compareWith to access this class instance.
   */
  compareWith = (o1: any, o2: any): boolean => {
    if (o1 === null || o2 === null || o1 === undefined || o2 === undefined) {
      return false;
    }

    const list = [o1, o2];
    const bothAreStrings = list.every((o: any) => typeof o === 'string');
    const bothAreNumbers = list.every((o: any) => typeof o === 'number');

    return bothAreStrings || bothAreNumbers || this.valueKey ? o1 === o2 : o1[this.labelKey] === o2[this.labelKey];
  };

  /*
   * Used for triggering required validator when the control is pristine, touched and invalid.
   */
  onPristineTouch(): void {
    if (this.control.pristine && this.control.touched && this.control.invalid) {
      this.control.markAsDirty({ onlySelf: true });
      this.control.updateValueAndValidity();
    }
  }

  selectionChange() {
    this.ngZone.run(() => {
      this.cd.detectChanges();
      this.matSelect.close();
    });
  }
}
